import axios from "axios";
import { getEnvironment } from "../utils/getEnvironment";

const environment = getEnvironment();

const api = axios.create({
    baseURL: environment.api_url,
    timeout: 20_000,
});

export type AppNameRequest = {
    businessType: string;
    language?: string;
    initial: boolean;
    userMessage?: string; // used when initial === false
    banList?: string[];
};

export type AppNameResponse = {
    name: string;
    meta: {
        businessType: string;
        language: string;
        initial: boolean;
    };
};

export type RGB = { red: number; green: number; blue: number };

export type LogoRequest = {
    brandName: string;
    businessType: string;
    userMessage: string;
    style?: string;
    color: RGB;
    size?: "512x512" | "1024x1024"; // optional, defaults server-side
};

export type LogoResponse = {
    url: string | null;
    meta: {
        brandName: string;
        businessType: string;
        size: "512x512" | "1024x1024";
    };
};

/**
 * Core call: POST { businessType, language?, initial, userMessage? }
 */
export async function requestAppName(
    payload: AppNameRequest
): Promise<AppNameResponse> {
    const { data } = await api.post<AppNameResponse>(
        "/aicompletions/app-name",
        payload,
        {
            headers: { "Content-Type": "application/json" },
        }
    );
    return data;
}

/** Convenience: first suggestion */
export async function requestInitialAppName(
    businessType: string,
    language = "English"
) {
    return requestAppName({ businessType, language, initial: true });
}

/** Convenience: refined suggestion with user's comment */
export async function requestRefinedAppName(
    businessType: string,
    userMessage: string,
    language = "English",
    banList?: string[]
) {
    return requestAppName({
        businessType,
        language,
        initial: false,
        userMessage,
        banList,
    });
}

/** Core call: POST /aicompletions/logo */
export async function requestLogoVariant(
    payload: LogoRequest
): Promise<LogoResponse> {
    const { data } = await api.post<LogoResponse>(
        "/aicompletions/logo",
        payload,
        {
            headers: { "Content-Type": "application/json" },
            timeout: 120_000, // <-- image gen + upload can exceed 20s
            maxBodyLength: Infinity,
            maxContentLength: Infinity,
        }
    );
    return data;
}

const AICompletions = {
    requestInitialAppName,
    requestRefinedAppName,
    requestLogoVariant,
};

export default AICompletions;
